import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { json, Form, redirect, Navigate, useActionData, useNavigate } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import './invite.css'

// COMPONENTS
import * as Icons from '@icons'
import Avatar from '@components/molecules/avatar'
import { Column } from '@components/templates/column'
import Button from '@components/atoms/button'
import Card from '@components/atoms/card'
import GroupBanner from '@components/organisims/group-banner'
import GroupProgressBar from '@/components/molecules/group-progress-bar'
import PledgeCell from '@components/molecules/pledge-cell'
import PledgeSlotFieldsMultiple from '@components/molecules/pledge-slot-fields-multiple'
import ErrorModal from '@components/molecules/error-modal'

// TYPES
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { createAction } from '@utils/actions'
import { parsePledge } from '@schemas'
import { getGroupStatus } from '@utils/temp-helpers'
import { isNonEmptyString } from '@utils/type-checkers'
import { createResponseObj, FailedResponse, isFailedResponse, VirtualCardAPI } from '@apis'
import { clamp, toPennies } from '@utils/number-helper'
import { min, max } from '@constants'
import { priceFormatter } from '@utils/number-helper'
import { useGroupLoaderData } from '..'
import { flatten } from '@utils/string-helper'
import { createAuthenticatedLoader } from '@utils/loaders'
import { useGroupSettings } from '../../_localStore/_hooks'
import { isDemoGroup } from '../demo-data'
import { toast } from 'sonner'


interface Props { }

const feeAmount = 0
const formId = "add-participant-slot"
const minSuggestedSplitCount = 1
const maxSuggestedSplitCount = 20
const splitCountIncludesCurrentUser = 1
const getPeopleSplittingCountStorageKey = (groupId: string) => `group-${groupId}-people-splitting-count`
const getSuggestedContributionStorageKey = (groupId: string) => `group-${groupId}-suggested-contribution`
const getParticipantOrganizerModalSeenStorageKey = (groupId: string) => `group-${groupId}-participant-organizer-modal-seen`
const clearSplitSuggestionStorage = (groupId: string) => {
    if (typeof window === 'undefined') return
    sessionStorage.removeItem(getPeopleSplittingCountStorageKey(groupId))
    sessionStorage.removeItem(getSuggestedContributionStorageKey(groupId))
    sessionStorage.removeItem(getParticipantOrganizerModalSeenStorageKey(groupId))
}
const GroupInviteScreen: FC<Props> = () => {
    const { isSignedIn, user } = useUser()
    const navigate = useNavigate()
    const actionData = useActionData()
    const group = useGroupLoaderData()
    const { resetGroupState, state } = useGroupSettings()
    const { isEditingParticipant, requestBody } = state
    const [pledgeSlots, setPledgeSlots] = useState<Pledge[]>([])
    const [showInput, setShowInput] = useState(true)
    const [isEditingPledge, setIsEditingPledge] = useState(false)
    const [isGroupOpen, setIsGroupOpen] = useState(false)
    const [showError, setError] = useState(false)
    const [error, setErrorData] = useState<FailedResponse>()
    const [suggestedContributionAmount, setSuggestedContributionAmount] = useState(0)
    const initializedForGroupRef = useRef<string>()

    const { groupStatus } = getGroupStatus(group)
    const isMerchant = !!user?.publicMetadata.merchant && !!group.merchant && user?.publicMetadata.merchant?.id === group.merchant?.id
    const isOrganizer = isSignedIn ? (group.organizer?.id === user.id) : false

    useEffect(() => {
        if (!!requestBody.title) resetGroupState()
    }, [])

    useEffect(() => {
        if (isFailedResponse(actionData)) {
            setErrorData(actionData)
            setError(true)
        }
    }, [actionData])

    useEffect(() => {
        if (initializedForGroupRef.current === group.id) return

        initializedForGroupRef.current = group.id

        const storedCount = Number(sessionStorage.getItem(getPeopleSplittingCountStorageKey(group.id)))
        const storedSuggestedAmount = Number(sessionStorage.getItem(getSuggestedContributionStorageKey(group.id)))
        const initialSlotCount = Number.isFinite(storedCount)
            ? Math.min(
                maxSuggestedSplitCount - splitCountIncludesCurrentUser,
                Math.max(minSuggestedSplitCount, Math.floor(storedCount) - splitCountIncludesCurrentUser),
            )
            : minSuggestedSplitCount
        const initialSuggestedAmount = Number.isFinite(storedSuggestedAmount)
            ? Math.max(0, Math.floor(storedSuggestedAmount))
            : 0

        setSuggestedContributionAmount(initialSuggestedAmount)

        setPledgeSlots(Array.from({ length: initialSlotCount }, () => parsePledge({ name: undefined, amount: initialSuggestedAmount })))
    }, [group.id])

    const isPledgeSlotComplete = (pledge: Pledge) => (!!pledge.name && pledge.amount >= min && pledge.amount <= max)

    const showAddAnother = useMemo(() => {
        if (isGroupOpen) return false
        if (pledgeSlots.length < 1) return false
        return isPledgeSlotComplete(pledgeSlots[pledgeSlots.length - 1])
    }, [pledgeSlots, isGroupOpen])

    const pledgesSubmitable = useMemo(() => {
        if (isGroupOpen) return true
        if (pledgeSlots.length < 1) return false
        return pledgeSlots.every((pledge) => isPledgeSlotComplete(pledge))
    }, [pledgeSlots, isGroupOpen])

    const { userPledges, claimedPledges, openPledges } = useMemo(() => [...(group.pledges || [])].reduce<{ userPledges: Pledge[], claimedPledges: Pledge[], openPledges: Pledge[] }>((prev, pledge) => {
        if (isSignedIn && isNonEmptyString(user.id)) {
            if (pledge.user?.id === user.id) return { ...prev, userPledges: [...prev.userPledges, pledge], claimedPledges: [...prev.claimedPledges, pledge] };
            else return { ...prev, claimedPledges: (!!pledge.transactionId || !!pledge.user?.id) ? [...prev.claimedPledges, pledge] : prev.claimedPledges, openPledges: (!pledge.transactionId && !pledge.user?.id) ? [...prev.openPledges, pledge] : prev.openPledges }
        } else return prev
    }, { userPledges: [], claimedPledges: [], openPledges: [] }), [isSignedIn, user?.id, group.pledges])

    const { addedPledgesTotal, openPledgesTotal } = useMemo(() => {
        let addedPledgesTotal = pledgeSlots.reduce((prev, current) => prev + current.amount, 0)
        let openPledgesTotal = openPledges.reduce((prev, current) => prev + current.amount, 0)

        return { addedPledgesTotal, openPledgesTotal }
    }, [openPledges, pledgeSlots])

    // Transaction information for the current group
    const transactions = useMemo(() => {
        const items = group.transactions
        if (!items) return []

        return Object.keys(items).map(key => items[key]).reduce((a, b) => a.concat(b), [])
    }, [group.transactions])
    const hasTransactions = useMemo(() => !!transactions && transactions?.length > 0, [transactions])

    const feeRef = useRef<HTMLDialogElement>(null)
    function openFeeInfo() { if (feeRef.current) feeRef.current.showModal() }
    function closeFeeInfo() { if (feeRef.current) feeRef.current.close() }

    function addPledgeSlot() {
        setShowInput(true)

        const pledge: Pledge = parsePledge({ name: undefined, amount: suggestedContributionAmount })
        setPledgeSlots((current) => [...current, pledge])
    }

    function editPledgeSlot(editing: boolean) {
        setIsEditingPledge(editing)
    }

    function removePledgeSlot(index: number) {
        setPledgeSlots((slots) => {
            const arr = [...slots]
            arr.splice(index, 1)
            return arr
        })
    }

    function updatePledgeSlot(index: number, name: string, amount: number) {
        const arr = [...pledgeSlots]
        arr[index] = parsePledge({ name: name || undefined, amount })
        setPledgeSlots(arr)
    }

    const isDemo = isDemoGroup(group.id)

    function handleBack() {
        group.type === 'pending'
            ? navigate('/account', { replace: true })
            : navigate(-1)
    }

    if (!isOrganizer || groupStatus !== 'active') return <Navigate to={`/group/${group.id}`} replace={true} />
    else return (<>
        <Column.Header.Start>
            <a onClick={handleBack}>
                <img src="/icons/arrow-left.svg" alt="back" style={{ width: '1.4rem', height: '1.4rem' }} draggable="false" />
            </a>
        </Column.Header.Start>
        <Column.Content className='group-banner-section'>
            <GroupBanner group={group} groupStatus={groupStatus} />
        </Column.Content>
        <Column.Content className="group-invite-screen">
            <GroupProgressBar goal={group.targetAmount || 0} pledge={((group.collectedAmount || 0) + openPledgesTotal + addedPledgesTotal) / 100} />

            {!isMerchant && <Card>
                <div className='flex flex-row items-center justify-between w-full'>
                    <div className='flex flex-row items-center gap-2'>
                        <Avatar user={user} style={{ height: '2.4rem', minWidth: '2.4rem' }} />
                        <p className='text-body'><strong>{user?.fullName}</strong></p>
                    </div>
                    <div className='flex flex-col items-end gap-2'>
                        {hasTransactions
                            ? !!group.spendAmount
                                ? <div style={{ display: 'inline' }}>
                                    <h2 style={{ display: 'inline' }}>{priceFormatter.format(userPledges[0].chargedToCard / 100)}</h2>
                                    <h2 style={{ display: 'inline' }}>&nbsp;/&nbsp;{priceFormatter.format(userPledges[0].amount / 100)}</h2>
                                </div>
                                : <h2 className='text-body'><strong>{priceFormatter.format(userPledges[0].amount / 100)}</strong></h2>
                            : <h2 className='text-body'><strong>{priceFormatter.format(userPledges[0].amount / 100)}</strong></h2>
                        }
                    </div>
                </div>
                <div className='flex flex-row items-center gap-2'>
                    <p className="text-body-2" style={{ color: 'var(--splitcard-gray-500)' }}>
                        <strong>{priceFormatter.format(feeAmount / 100)}&nbsp;</strong>
                        {groupStatus === 'active'
                            ? 'is your estimated fee.'
                            : ((groupStatus === 'completed' || groupStatus === 'finalized')
                                ? `is your ${groupStatus === 'completed' ? 'pending' : ''} fee based on your share of the cost.`
                                : '')}
                    </p>
                    <Icons.Info onClick={openFeeInfo} type='filled' height="1rem" width="1rem" style={{ color: 'var(--splitcard-blue-500)', cursor: 'pointer' }} />
                    <dialog ref={feeRef}>
                        <div>
                            <div className="flex flex-col items-stretch" style={{ maxWidth: 'calc(var(--max-content-width, 610px) - 8rem)', textAlign: 'left', margin: '1rem', background: 'var(--splitcard-white)', gap: '1rem', padding: '1.3125rem', borderRadius: 'var(--border-radius)', overflowY: 'auto' }}>
                                <h2>Splitcard Fee</h2>
                                <p><strong>Good news — we’ve waived our fee!</strong> Splitcard is free to use for a limited time.</p>
                                <p>Now’s the perfect time to try it and <strong>share with friends and family</strong></p>
                                <div className="flex flex-col items-stretch gap-2.5">
                                    <Button theme="primary" formMethod="dialog" onClick={closeFeeInfo}>Got it!</Button>
                                </div>
                            </div>
                        </div>
                    </dialog>
                </div>
            </Card>}

            {((group.type !== 'merchant' && claimedPledges.length > 1) 
                || (group.type === 'merchant' && isMerchant && claimedPledges.length > 0) 
                || (group.type === 'merchant' && !isMerchant && claimedPledges.length > 1)) &&
                <Card>
                    {claimedPledges.filter((pledge) => pledge.user?.id !== user?.id).map((pledge, index) =>
                        <PledgeCell
                            key={`${pledge.user?.id}-${index}`}
                            group={group}
                            pledge={pledge}
                            verified={(group.organizer.id === pledge.user?.id)}
                            style={{ color: 'var(--splitcard-gray-500)', marginTop: index === 0 ? undefined : '1rem' }}
                        >
                        </PledgeCell>
                    )}
                </Card>
            }

            <Form id={formId} method="POST" className='flex flex-col'>
                <input name="isGroupOpen" value={isGroupOpen.toString()} readOnly hidden />
                <Card className={flatten('flex flex-col', 'open-participant-card', isGroupOpen ? 'disable' : '', pledgeSlots.length === 1 ? 'empty-open-pledge-list' : '')}>
                    {openPledges.length > 0 && <>
                        <p className='text-body text-center'><strong>Participant(s) that have not joined yet</strong></p>
                        {openPledges.map((pledge, index) =>
                            <PledgeCell
                                key={`${pledge.user?.id}-${index}`}
                                group={group}
                                pledge={pledge}
                                verified={(group.organizer.id === pledge.user?.id)}
                                style={{ color: 'var(--splitcard-gray-500)', marginTop: index === 0 ? undefined : '1rem' }}
                            >
                            </PledgeCell>
                        )}
                    </>}
                    <p className='text-body text-gray-500 text-center'><strong>Add participant(s)</strong></p>
                    {showInput && pledgeSlots.map((pledge, index) => (
                        <React.Fragment key={index}>
                            <PledgeSlotFieldsMultiple
                                group={group}
                                pledge={pledge}
                                pledgeSlots={pledgeSlots}
                                hidePledge={isEditingPledge && ((index + 1) === pledgeSlots.length)}
                                showRemove={index > 0}
                                addedPledge={false}
                                onPledgeEdit={(editing) => editPledgeSlot(editing)}
                                onPledgeChange={(name, amount) => updatePledgeSlot(index, name, amount)}
                                onPledgeRemove={() => removePledgeSlot(index)}
                            />
                        </React.Fragment>
                    ))}
                    {!isEditingPledge && showAddAnother && <div className='flex flex-row items-center justify-center text-blue-500' style={{ gap: '0.75rem', alignSelf: 'flex-start', cursor: 'pointer', width: '100%' }} onClick={addPledgeSlot}>
                        <Icons.Plus width="1.25rem" height="1.25rem" />
                        <p><strong>Add Another</strong></p>
                    </div>}
                </Card>
                {group.type === 'pending' && <div className={flatten('type-cell', isGroupOpen ? 'selected' : '')} onClick={() => setIsGroupOpen(!isGroupOpen)}>
                    <p className='text-body text-gray-500 text-center flex-1'><strong>Invite participants later</strong></p>
                    <Icons.Chevron width="1.4rem" height="1.4rem" facing={'right'} />
                </div>}
            </Form>
            {!isEditingParticipant && <Column.Footer className='group-share'>
                <Button theme='primary' form={isDemo ? undefined : formId} type={isDemo ? 'button' : 'submit'} disabled={!pledgesSubmitable} onClick={isDemo ? () => toast.info('This action is disabled in the demo') : undefined}>{isGroupOpen ? 'Next' : 'Add Participant(s)'}</Button>
            </Column.Footer>}
            <ErrorModal
                state={[showError, setError]}
                error={error}
                title="Issue Updating Participants"
                fallbackMessage="There was an issue updating participants in this group. Please try again."
            />
        </Column.Content>
    </>)
}

const action = createAction(async ({ request, params }) => {
    try {
        const formData = await request.formData()
        const isGroupOpen = formData.get('isGroupOpen') === 'true'
        const groupId = params['groupId']

        console.log("Add Pledge Data:", { groupId, isGroupOpen })
        if (!isNonEmptyString(groupId)) throw createResponseObj({
            status: 'failed', type: 'local', name: 'missing-resources',
            message: `Missing required resources. ${JSON.stringify({ groupId })}`
        })

        if (isGroupOpen) {
            await VirtualCardAPI.UpdateGroupRequest.fetch(groupId, { type: 'open' })
            return redirect('../?fromInvite=true')
        }

        const names = formData.getAll('name')
        const amounts = formData.getAll('amount')

        const promises = names.reduce<Promise<Pledge>[]>((prev, name, index) => {
            const amount = amounts.at(index)
            if (!name || !amount) return prev

            const pledge = parsePledge({
                name: `${name}`,
                amount: clamp(min, toPennies(`${amount}`), max),
            })

            return [...prev, VirtualCardAPI.AddPledgeRequest.fetch(groupId, pledge)]
        }, [])

        await Promise.all(promises)
        clearSplitSuggestionStorage(groupId)
        return redirect('../?fromInvite=true')
    } catch (error) {
        console.error(error)
        if (isFailedResponse(error)) return error

        return createResponseObj({
            status: 'failed',
            type: 'local',
            name: 'add-participant-error',
            message: 'There was an issue updating participants in this group. Please try again.',
        })
    }
})

const loader = createAuthenticatedLoader(() => json(null, 200))

const InviteRoute: RouteObject = {
    path: "invite",
    element: <GroupInviteScreen />,
    loader,
    action,
}

export default InviteRoute
